/**
 * Sessão do médico na estação (WES Identity, PBI-07).
 *
 * Quem decide o estado é o servidor: `GET /identity/session/state` diz se a
 * sessão está ativa, mascarada ou encerrada, e esta camada só obedece. Os
 * tempos de mascarar e expirar (15 e 30 minutos no backend) não aparecem em
 * lugar nenhum daqui — o protótipo usa 5 e 20 com timers no navegador, e
 * copiar isso faria a tela e o servidor discordarem sobre quem está logado.
 *
 * Três regras que valem para qualquer mudança neste arquivo:
 *
 * - consultar o estado **não** é atividade. Só interação real (clique, tecla,
 *   rolagem) vira heartbeat. Senão a tela que pergunta mantém a sessão viva
 *   para sempre e o mascaramento nunca acontece;
 * - sessão mascarada **não** volta por atividade, só por prova (PIN ou rosto).
 *   Por isso o heartbeat para enquanto a máscara está de pé;
 * - quem entrou por senha tem estado `none`. Isso não é "encerrada": é "não
 *   há sessão de estação", e a tela não faz nada.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { WES_IDENTITY_DEMO } from '../config/runtimeConfig';
import AppToast, { toastDurationFor } from '../components/common/AppToast';
import {
  END_REASON_HANDOVER,
  END_REASON_TIMEOUT,
  SESSION_ACTIVE,
  SESSION_ENDED,
  SESSION_MASKED,
  getSessionState,
  handoverSession,
  reverifyWithFace,
  reverifyWithPin,
  sendHeartbeat,
} from '../services/identityService';

// Com que frequência perguntar ao servidor. O limiar de mascarar é de
// minutos; 15 s de atraso na máscara é imperceptível e não pesa no backend.
const POLL_MS = 15000;
// Atividade real é agrupada: um heartbeat por minuto basta para o servidor,
// que mede inatividade em minutos.
const HEARTBEAT_MIN_INTERVAL_MS = 60000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll'];

// O botão "Ocultar sessão" existe para demonstrar o mascaramento sem esperar
// minutos parado. Não existe no produto: só no build do HML.
// Runtime, nao build: ver o comentario em `config/runtimeConfig.js`.
export const IDENTITY_DEMO_ENABLED = WES_IDENTITY_DEMO === 'true';

// Motivos de encerramento (models/identity_session.py) que o médico precisa
// ler na tela de login. `logout` foi ele mesmo; não precisa de aviso.
const END_MESSAGES = {
  [END_REASON_TIMEOUT]: 'identity.session.expired',
  [END_REASON_HANDOVER]: 'identity.session.replaced',
};

// Ações sensíveis, pela lista combinada com o backend (B7). Explícita de
// propósito: casar pelo texto do botão, como o protótipo faz raspando o DOM,
// quebraria na primeira tradução. Hoje só EXPORT_DATA e DOWNLOAD_PDF têm tela;
// PRESCRIBE e SIGN_DOCUMENT já ficam aqui para quem construir essas telas.
export const SENSITIVE_ACTIONS = {
  PRESCRIBE: 'prescribe',
  SIGN_DOCUMENT: 'sign_document',
  EXPORT_DATA: 'export_data',
  DOWNLOAD_PDF: 'download_pdf',
};

// Os dois bloqueios têm motivos diferentes, e a tela diz qual: no restrito a
// operadora não respondeu; no PIN a prova foi mais fraca que o rosto.
const BLOCK_RESTRICTED = 'restricted';
const BLOCK_PIN = 'pin';

const IdentitySessionContext = createContext(null);

export function IdentitySessionProvider({ children }) {
  const { t } = useTranslation();
  const { authenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [demoMasked, setDemoMasked] = useState(false);
  // 404 quer dizer que a POC não está acesa para este tenant (ou no
  // ambiente). Não é erro: é o interruptor funcionando, e a tela some.
  const [available, setAvailable] = useState(true);
  const [blockedToast, setBlockedToast] = useState(null);
  const lastHeartbeat = useRef(0);
  const leaving = useRef(false);

  const leave = useCallback(
    async (messageKey) => {
      if (leaving.current) return;
      leaving.current = true;
      try {
        setSession(null);
        setDemoMasked(false);
        await logout();
        // A LoginPage já mostra `state.messageKey`; nada a mudar nela.
        navigate('/login', { replace: true, state: messageKey ? { messageKey } : undefined });
      } finally {
        // O `finally` não é zelo: `leaving.current` é uma trava de reentrada,
        // e sem ele uma rejeição do `logout()` a deixava travada para sempre.
        // A partir daí `leave` retornava cedo em toda chamada seguinte — a
        // sessão nunca mais terminava, nem por inatividade, nem por handover.
        leaving.current = false;
      }
    },
    [logout, navigate],
  );

  const apply = useCallback(
    (next) => {
      setSession(next);
      if (next && next.state === SESSION_ENDED && next.requires_login) {
        leave(END_MESSAGES[next.end_reason] || null);
      }
    },
    [leave],
  );

  const refresh = useCallback(async () => {
    try {
      apply(await getSessionState());
    } catch (error) {
      if (error && error.response && error.response.status === 404) {
        setAvailable(false);
      }
      // Outras falhas (rede, 5xx) mantêm o último estado conhecido: tirar a
      // máscara por um pacote perdido seria pior que atrasá-la.
    }
  }, [apply]);

  useEffect(() => {
    if (!authenticated) {
      // Outra pessoa pode entrar em outro tenant, onde a POC está acesa.
      setSession(null);
      setAvailable(true);
      return undefined;
    }
    if (!available) {
      setSession(null);
      return undefined;
    }
    refresh();
    const timer = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(timer);
  }, [authenticated, available, refresh]);

  const hasStationSession = Boolean(
    session && (session.state === SESSION_ACTIVE || session.state === SESSION_MASKED),
  );
  const masked = hasStationSession && (session.state === SESSION_MASKED || demoMasked);

  useEffect(() => {
    if (!hasStationSession || masked) return undefined;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastHeartbeat.current < HEARTBEAT_MIN_INTERVAL_MS) return;
      lastHeartbeat.current = now;
      sendHeartbeat().then(apply).catch(() => {});
    };
    ACTIVITY_EVENTS.forEach((name) => document.addEventListener(name, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((name) => document.removeEventListener(name, onActivity));
    };
  }, [hasStationSession, masked, apply]);

  const reverifyPin = useCallback(
    async (pin) => {
      const next = await reverifyWithPin(pin);
      setDemoMasked(false);
      apply(next);
      return next;
    },
    [apply],
  );

  const reverifyFace = useCallback(
    async (blob) => {
      const next = await reverifyWithFace(blob);
      setDemoMasked(false);
      apply(next);
      return next;
    },
    [apply],
  );

  const handover = useCallback(async () => {
    try {
      await handoverSession();
    } catch (error) {
      // Mesmo se a chamada falhar, a sessão do médico anterior não pode
      // continuar na tela de quem acabou de dizer "sou outra pessoa". O
      // logout abaixo apaga os cookies de qualquer forma; o servidor fecha a
      // sessão da estação por inatividade.
    }
    // Sem mensagem: quem chega vê o login limpo, não o motivo de quem saiu.
    await leave(null);
  }, [leave]);

  // Bloqueio visual das ações sensíveis. Na POC é só visual, e isso é
  // limitação assumida, não esquecimento: o backend não recusa as ações.
  let blockReason = null;
  if (session && session.restricted) blockReason = BLOCK_RESTRICTED;
  else if (session && session.sensitive_actions_blocked) blockReason = BLOCK_PIN;

  const guardSensitive = useCallback(
    (action, handler) => (...args) => {
      if (blockReason) {
        setBlockedToast({ message: t(`identity.blocked.${blockReason}`), severity: 'warning' });
        return undefined;
      }
      return handler(...args);
    },
    [blockReason, t],
  );

  const maskForDemo = useCallback(() => {
    if (IDENTITY_DEMO_ENABLED) setDemoMasked(true);
  }, []);

  const value = useMemo(
    () => ({
      hasStationSession,
      masked,
      fullName: session ? session.full_name : null,
      restricted: Boolean(session && session.restricted),
      sensitiveActionsBlocked: Boolean(session && session.sensitive_actions_blocked),
      stationRoom: session ? session.station_room : null,
      stationUnit: session ? session.station_unit : null,
      reverifyPin,
      reverifyFace,
      handover,
      maskForDemo,
      demoEnabled: IDENTITY_DEMO_ENABLED,
      sensitiveBlocked: Boolean(blockReason),
      guardSensitive,
      // A POC está acesa para este usuário: o estado respondeu (inclusive
      // `none`, de quem entrou por senha) em vez de 404.
      identityAvailable: available && Boolean(session),
    }),
    [
      hasStationSession, masked, session, reverifyPin, reverifyFace, handover,
      maskForDemo, blockReason, guardSensitive, available,
    ],
  );

  return (
    <IdentitySessionContext.Provider value={value}>
      {children}
      <AppToast
        toast={blockedToast}
        onClose={() => setBlockedToast(null)}
        autoHideDuration={toastDurationFor(blockedToast && blockedToast.message)}
      />
    </IdentitySessionContext.Provider>
  );
}

// Fora do provider (telas públicas, testes) a resposta é "sem sessão de
// estação", e não um erro: a maior parte do Hub nunca vai ter uma.
const NO_SESSION = {
  hasStationSession: false,
  masked: false,
  fullName: null,
  restricted: false,
  sensitiveActionsBlocked: false,
  stationRoom: null,
  stationUnit: null,
  reverifyPin: async () => null,
  reverifyFace: async () => null,
  handover: async () => {},
  maskForDemo: () => {},
  demoEnabled: false,
  sensitiveBlocked: false,
  guardSensitive: (action, handler) => handler,
  identityAvailable: false,
};

export function useIdentitySession() {
  return useContext(IdentitySessionContext) || NO_SESSION;
}

/** "Dra. Helena Prado" -> "Helena", para o "Continuar como". */
export function firstNameOf(fullName) {
  if (!fullName) return '';
  const parts = String(fullName).trim().split(/\s+/);
  const withoutTitle = parts.filter((part) => !/\.$/.test(part));
  return withoutTitle[0] || parts[0];
}

/**
 * Para um botão de ação sensível: se a sessão bloqueia, o botão fica com
 * aparência de indisponível e o clique explica o porquê em vez de executar.
 * Continua clicável de propósito — um botão desabilitado não diz nada.
 *
 *     const exportAction = useSensitiveAction(SENSITIVE_ACTIONS.EXPORT_DATA);
 *     <button className={`btn primary${exportAction.className}`}
 *       aria-disabled={exportAction.ariaDisabled}
 *       onClick={exportAction.wrap(() => setExportOpen(true))}>
 */
export function useSensitiveAction(action) {
  const { sensitiveBlocked, guardSensitive } = useIdentitySession();
  return {
    blocked: sensitiveBlocked,
    className: sensitiveBlocked ? ' identity-action-is-restricted' : '',
    ariaDisabled: sensitiveBlocked ? 'true' : undefined,
    wrap: (handler) => guardSensitive(action, handler),
  };
}
